<template>
  <div class="modal fade show d-block" tabindex="-1" style="background-color: rgba(0,0,0,0.5);">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            {{ isEdit ? $t('edit_planning') : $t('create_planning') }}
          </h5>
          <button type="button" class="btn-close" :aria-label="$t('close')" @click="$emit('close')"></button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="save">
            <div class="row">
              <div class="col-md-12 mb-3">
                <label class="form-label fw-bold">{{ $t('title') }} *</label>
                <input v-model="form.title" type="text" class="form-control" required :placeholder="$t('title')">
              </div>

              <div class="col-md-12 mb-3">
                <label class="form-label fw-bold">{{ $t('description') }}</label>
                <textarea v-model="form.description" class="form-control" rows="2" :placeholder="$t('description')"></textarea>
              </div>

              <div class="col-md-4 mb-3">
                <label class="form-label fw-bold">{{ $t('assigned_to') }} *</label>
                <select v-model="form.assigned_user_id" class="form-select" required>
                  <option value="" disabled>{{ $t('select_user') }}</option>
                  <option v-for="user in users" :key="user.id" :value="user.id">
                    {{ user.name }}
                  </option>
                </select>
              </div>

              <div class="col-md-4 mb-3">
                <label class="form-label fw-bold">{{ $t('client') }}</label>
                <auto-complete
                  :default-value="selectedClientName"
                  :dislay-suggestions="true"
                  entity="clients"
                  emit-event="emitAutoCompleteClient"
                  @emitAutoCompleteClient="emitAutoCompleteClient"
                />
              </div>

              <div class="col-md-4 mb-3">
                <label class="form-label fw-bold">{{ $t('priority') }}</label>
                <select v-model="form.priority" class="form-select">
                  <option value="low">{{ $t('low') }}</option>
                  <option value="medium">{{ $t('medium') }}</option>
                  <option value="high">{{ $t('high') }}</option>
                  <option value="urgent">{{ $t('urgent') }}</option>
                </select>
              </div>

              <div class="col-md-4 mb-3">
                <label class="form-label fw-bold">{{ $t('date') }} *</label>
                <input v-model="form.date" type="date" class="form-control" required>
              </div>

              <div class="col-md-4 mb-3">
                <label class="form-label fw-bold">{{ $t('start_time') }}</label>
                <input v-model="form.start_time" type="time" class="form-control">
              </div>

              <div class="col-md-4 mb-3">
                <label class="form-label fw-bold">{{ $t('end_time') }}</label>
                <input v-model="form.end_time" type="time" class="form-control">
              </div>

              <div class="col-md-12 mb-3">
                <label class="form-label fw-bold">{{ $t('address') }}</label>
                <input v-model="form.address" type="text" class="form-control" :placeholder="$t('address')">
              </div>

              <div class="col-md-12 mb-3">
                <label class="form-label fw-bold">{{ $t('instructions') }}</label>
                <textarea v-model="form.instructions" class="form-control" rows="3" :placeholder="$t('instructions')"></textarea>
              </div>
            </div>

            <div v-if="isEdit" class="col-md-12 mb-3">
              <label class="form-label fw-bold">{{ $t('status') }}</label>
              <select v-model="form.status" class="form-select">
                <option value="planned">{{ $t('planned') }}</option>
                <option value="in_progress">{{ $t('in_progress') }}</option>
                <option value="completed">{{ $t('completed') }}</option>
                <option value="failed">{{ $t('failed') }}</option>
                <option value="postponed">{{ $t('postponed') }}</option>
              </select>
            </div>
          </form>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="$emit('close')">{{ $t('cancel') }}</button>
          <button type="button" class="btn btn-success" :disabled="loading" @click="save">
            <span v-if="loading" class="spinner-border spinner-border-sm me-1"></span>
            {{ isEdit ? $t('update') : $t('save') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import helper from '../../helper'
import AutoComplete from '../autocomplete/AutoComplete.vue'

export default {
  name: 'AddEditPlanningModal',
  components: {
    AutoComplete
  },
  props: {
    planning: {
      type: Object,
      default: null
    },
    initialDate: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      loading: false,
      users: [],
      selectedClientName: this.planning && this.planning.client ? this.planning.client.name : '',
      form: {
        title: '',
        description: '',
        assigned_user_id: '',
        client_id: '',
        priority: 'medium',
        status: 'planned',
        date: this.initialDate || new Date().toISOString().split('T')[0],
        start_time: '',
        end_time: '',
        address: '',
        instructions: ''
      }
    }
  },
  computed: {
    isEdit() {
      return !!this.planning
    }
  },
  async created() {
    await this.getUsers()
    if (this.planning) {
      this.form = {
        title: this.planning.title || '',
        description: this.planning.description || '',
        assigned_user_id: this.planning.assigned_user_id || '',
        client_id: this.planning.client_id || '',
        priority: this.planning.priority || 'medium',
        status: this.planning.status || 'planned',
        date: this.planning.date || '',
        start_time: this.planning.start_time || '',
        end_time: this.planning.end_time || '',
        address: this.planning.address || '',
        instructions: this.planning.instructions || ''
      }
    } else {
      this.setCurrentUserAsAssignee()
    }
  },
  methods: {
    async getUsers() {
      try {
        const response = await helper.customGet(`${process.env.VUE_APP_API}/users`, { params: { per_page: 100 } })
        this.users = response.data.data || []
      } catch (error) {
        alert(this.$t('error_fetching_users'))
      }
    },
    setCurrentUserAsAssignee() {
      const currentUser = this.$store.getters.USER
      if (!currentUser || !currentUser.id) return

      const userInList = this.users.find(user => String(user.id) === String(currentUser.id))
      if (!userInList) {
        this.users.unshift(currentUser)
      }
      this.form.assigned_user_id = userInList ? userInList.id : currentUser.id
    },
    emitAutoCompleteClient(data) {
      if (data && data.result) {
        this.form.client_id = data.result.id
        this.selectedClientName = data.result.name || ''
        if (data.result.address) {
          this.form.address = data.result.address
        }
      } else {
        this.form.client_id = ''
        this.selectedClientName = ''
      }
    },
    async save() {
      if (!this.form.title || !this.form.assigned_user_id || !this.form.date) {
        helper.showToast(this.$t('please_fill_all_required_fields'), 'error')
        return
      }

      this.loading = true
      try {
        if (this.isEdit) {
          await helper.customPatch(`${process.env.VUE_APP_API}/plannings/${this.planning.id}`, this.form, { showToast: true })
        } else {
          await helper.customPost(`${process.env.VUE_APP_API}/plannings`, this.form, { showToast: true })
        }
        this.$emit('refresh')
        this.$emit('close')
      } catch (error) {
        alert(this.$t('error_saving_planning'))
      } finally {
        this.loading = false
      }
    }
  }
}
</script>
